import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {staticUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedButton from "#components/button-package/deprecated-button.tsx";

import {withStrings} from "./locales/ambassador-landing/load-strings.tsx";
import sharedStyles from "./styles.ts";
import ThumbnailBlock from "./thumbnail-block.tsx";

enum ApplicationReviewCycleStatus {
    CanApply = "open",
    Waitlist = "waitlist",
    NotAcceptingApplications = "closed",
}

class ApplicationReviewCycle {
    //property type specification and defaults
    status = ApplicationReviewCycleStatus.CanApply;
    startDate: Date = new Date(2018, 1, 1, 0, 0, 0);
    endDate: Date = new Date(2018, 12, 31, 0, 0, 0);

    //TODO: allow start and end date to be optional
    constructor(args: {
        status: ApplicationReviewCycleStatus;
        startDate: Date;
        endDate: Date;
    }) {
        if (args !== undefined) {
            if (args.status !== undefined) {
                this.status = args.status;
            }

            if (
                args.startDate !== undefined &&
                args.endDate !== undefined &&
                this.startDate < this.endDate
            ) {
                this.startDate = args.startDate;
                this.endDate = args.endDate;
            }
        }
    }

    hasOpenWaitlist(): boolean {
        return this.status === ApplicationReviewCycleStatus.Waitlist;
    }

    //could be modified to accept a date param, so that future dates can also be checked
    isAcceptingApplications(): boolean {
        const currentDate = new Date();
        return (
            currentDate >= this.startDate &&
            currentDate < this.endDate &&
            this.status === ApplicationReviewCycleStatus.CanApply
        );
    }
}

export const CURRENT_APP_REVIEW_CYCLE = new ApplicationReviewCycle({
    status: ApplicationReviewCycleStatus.CanApply,
    startDate: new Date(2019, 4, 28, 0, 0, 0),
    endDate: new Date(2019, 5, 30, 0, 0, 0),
});

type ApplyHereProps = {
    appReviewCycle: ApplicationReviewCycle;
};

class ApplyHere extends React.Component<ApplyHereProps> {
    render(): React.ReactNode {
        const reviewCycle = this.props.appReviewCycle;

        const acceptingUrl = t({
            context:
                "Link on /ambassador, when English site is accepting new applications",
            message: "https://khanacademy.wufoo.com/forms/m4l36ta1kwdzcz/",
        });

        const waitlistUrl = t({
            context:
                "Link on /ambassador, when English site is not accepting new applications",
            message: "https://khanacademy.wufoo.com/forms/zccnv0r1bitlvl/",
        });

        //apply now
        if (reviewCycle.isAcceptingApplications()) {
            return (
                <div className={css(styles.applyHere)}>
                    <DeprecatedButton type="primary" href={acceptingUrl}>
                        {t`Apply here now!`}
                    </DeprecatedButton>
                </div>
            );
        }
        if (reviewCycle.hasOpenWaitlist()) {
            //can be waitlisted
            return (
                <div className={css(styles.applyHere)}>
                    <DeprecatedButton type="primary" href={waitlistUrl}>
                        {t`Let us know you are interested!`}
                    </DeprecatedButton>
                </div>
            );
        }
        //can no longer apply
        return (
            <div className={css(styles.applyHere)}>
                <DeprecatedButton type="primary" disabled>
                    {t`Applications are closed. Check back soon.`}
                </DeprecatedButton>
            </div>
        );
    }
}

type HeaderProps = {
    appReviewCycle: ApplicationReviewCycle;
};

class Header extends React.Component<HeaderProps> {
    render(): React.ReactNode {
        const heading = (
            <div>
                <h1 className={css(styles.title)}>
                    {t`Become a Khan Academy Ambassador`}
                </h1>
            </div>
        );

        const contentCopy = this.props.appReviewCycle.isAcceptingApplications()
            ? t`Share your enthusiasm for providing a free, world-class education for all while growing personally and professionally: join our network of inspiring educators who are spreading their expertise to teachers and classrooms around the United States!`
            : t`Applications for the Khan Academy Ambassador Program are currently closed. Please fill out the survey below to let us know you are interested! We will notify you when applications re-open.`;

        const content = (
            <div>
                <div className={css(styles.description)}>{contentCopy}</div>

                <ApplyHere appReviewCycle={this.props.appReviewCycle} />
            </div>
        );

        return (
            <div className={css(styles.contentModule)}>
                <img
                    alt=""
                    aria-hidden={true}
                    className={css(styles.headerLogo)}
                    src={staticUrl("/images/referrals/rocket-only.svg")}
                />
                {heading}
                {content}
            </div>
        );
    }
}

type DefaultProps = {
    appReviewCycle: ApplicationReviewCycle;
};

type AmbassadorLandingProps = DefaultProps;

class AmbassadorLanding extends React.Component<AmbassadorLandingProps> {
    static defaultProps: DefaultProps = {
        appReviewCycle: CURRENT_APP_REVIEW_CYCLE,
    };

    render(): React.ReactNode {
        return (
            <PageRoot
                settings={{
                    title: t`Khan Academy Ambassador Program`,
                }}
            >
                <div>
                    <div className={css(styles.headerWrapper)}>
                        <Header appReviewCycle={this.props.appReviewCycle} />
                    </div>
                    <h2 className={css(styles.contentTitle)}>
                        {t`Who are Khan Academy Ambassadors?`}
                    </h2>
                    <p className={css(styles.content)}>
                        {t`Khan Academy Ambassadors are ambitious, energetic, and knowledgeable educators who are experts in using Khan Academy within a classroom context. They believe deeply in our mission and are ready to super-charge student practice on Khan Academy throughout their school and community during the 2019-2020 school year. We’ll provide you with Khan Academy materials and a powerful community, and equip you to share Khan Academy and support its use however best suits your style.`}
                    </p>
                    <p className={css(styles.content)}>
                        {t`We believe that educators are THE difference-makers in the classroom and beyond. Our team cannot wait to partner with Khan Academy Ambassadors to learn and grow as an organization and, together, to empower educators and students across the country!`}
                    </p>

                    <h2 className={css(styles.contentTitle)}>
                        {t`What can Ambassadors do?`}
                    </h2>

                    <p className={css(styles.content)}>
                        {t`All ambassadors are expected to share Khan Academy and support its use within their school and online communities. Here are some ways you might do that:`}
                    </p>

                    <div className={css(styles.thumbnails)}>
                        <div className={css(styles.thumbnailsContainer)}>
                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="teachers-leaf-tablet.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Serve as “go-to” Khan Academy leader in your school community`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Support peers with getting Khan Academy kicked off and used regularly in the classroom, sharing your tips and tricks along the way`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="calendar.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>{t`Lead trainings and PD sessions`}</h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Introduce Khan Academy in the ways you find most impactful, like through school or district-wide PD sessions, lunch-n-learns, or 1:1 onboarding sessions`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="rocket-1.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Spread our mission of a free, world-class education through online channels`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Make people aware of Khan Academy’s mission, products, and initiatives by leading chats, posting on social media, writing blog posts, and more`}
                                    </p>
                                </div>
                            </ThumbnailBlock>
                        </div>

                        <div className={css(styles.thumbnailsContainer)}>
                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="civil-rights.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Engage with other Ambassadors within our network`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Exchange best practices and learn from fellow leaders in the field`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="laptop-handtree.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Help fellow educators on online support forums`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`If you love providing specific Khan Academy guidance to educators across the world, you can work directly with our staff by answering questions in our online community forum`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="presentation.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Present at conferences and education events`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Share your professional expertise and represent Khan Academy along the way`}
                                    </p>
                                </div>
                            </ThumbnailBlock>
                        </div>
                    </div>

                    <h2 className={css(styles.contentTitle, styles.perks)}>
                        {t`What are the perks of being an Ambassador?`}
                    </h2>

                    <div className={css(styles.thumbnails)}>
                        <div className={css(styles.thumbnailsContainer)}>
                            <ThumbnailBlock isIntro={true} imgPath="globe.svg">
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Make a critical impact in advancing educational equity`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Receive resources and support to make a huge, powerful difference in the lives of educators and students`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="three-teachers.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Build your professional network`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Membership in a powerful, private network of expert teachers and leaders. Join and lead conversations about education topics important to you`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="megaphone.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>{t`Make your voice heard`}</h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Direct access and ability to give feedback to Khan Academy staff in order to directly impact what we build for millions of learners and educators!`}
                                    </p>
                                </div>
                            </ThumbnailBlock>
                        </div>

                        <div className={css(styles.thumbnailsContainer)}>
                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="sat_laptop-gradcap.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>{t`Receive early, priority access`}</h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`First chance to test out new features and products whenever possible`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock isIntro={true} imgPath="ribbon.svg">
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Receive swag and special opportunities`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`Special gifts from our Khan Academy team to rep your role as an Ambassador, as well as access to Ambassador-specific opportunities throughout the year`}
                                    </p>
                                </div>
                            </ThumbnailBlock>

                            <ThumbnailBlock
                                isIntro={true}
                                imgPath="KA-Ambassador-Logo.svg"
                            >
                                <div className={css(styles.thumbnailText)}>
                                    <h3>
                                        {t`Share your title and get recognized for your great work`}
                                    </h3>
                                    <p className={css(styles.thumbnailBody)}>
                                        {t`An official Khan Academy Ambassador badge for your communications and listing on our website’s Ambassador directory. Shout outs in newsletters and media opportunities when available`}
                                    </p>
                                </div>
                            </ThumbnailBlock>
                        </div>
                    </div>

                    <ApplyHere appReviewCycle={this.props.appReviewCycle} />

                    <p className={css(styles.content, styles.footer)}>
                        {t`All K-12 teachers, instructional coaches, or school and district administrators in the United States who currently use Khan Academy are eligible to apply. This is a volunteer opportunity. If you have any questions, please contact us at`}{" "}
                        <a href="mailto:ambassadors@khanacademy.org">
                            {t`ambassadors@khanacademy.org`}
                        </a>
                    </p>

                    <p className={css(styles.content, styles.disclaimer)}>
                        {t`Note: Space is limited and, as much as we wish we had capacity to work closely with everyone, we will not be able to accept all eligible applicants into this year’s program. We will close applications after all spots are filled.`}
                    </p>
                </div>
            </PageRoot>
        );
    }
}

export default withStrings(AmbassadorLanding);

const styles = StyleSheet.create({
    ...sharedStyles,
    headerLogo: {
        margin: "2em auto",
        maxWidth: 105,
    },
    thumbnails: {
        margin: "0 auto",
        maxWidth: 1200,
    },
    contentTitle: {
        margin: "60px auto 16px auto",
        maxWidth: 560,
        textAlign: "center",
    },
    applyHere: {
        textAlign: "center",
    },
    disclaimer: {
        fontWeight: "bold",
        textAlign: "center",
        margin: "60px auto",
    },
    footer: {
        textAlign: "center",
        margin: "60px auto",
    },
    perks: {
        marginTop: "68px",
        marginBottom: "20px",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    contentBottom: {
        marginBottom: "16px",
    },
    thumbnailBody: {
        fontSize: 16,
        textAlign: "left",
        margin: "1em",
    },
});
